// Scream Room — client for the backend Scream Room API (admin-service-api).
//
// Shared, ephemeral room: the backend stores messages in Redis keyed by UTC day
// (auto-expires, so it refreshes daily). This client polls for near-realtime and
// posts via the same admin session FAST already uses (cookie credentials). No
// Firestore. Same interface the page depends on:
//
//   subscribeToScreamRoom(onMessages, onError?) -> unsubscribe()  // oldest -> newest
//   postToScreamRoom({ text, feel }) -> Promise
//   subscribeToPresence(onRoster, onError?) -> unsubscribe()      // who's online/away

const BASE = (process.env.REACT_APP_BACKEND_BASE_URL || '').replace(/\/+$/, '')
const ENDPOINT = `${BASE}/1.0/scream-room`
const PRESENCE_ENDPOINT = `${ENDPOINT}/presence`
const POLL_MS = 3000
const PRESENCE_MS = 10000

async function getMessages() {
  const res = await fetch(ENDPOINT, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`scream-room GET ${res.status}`)
  const data = await res.json()
  return Array.isArray(data.messages) ? data.messages : []
}

export function subscribeToScreamRoom(onMessages, onError) {
  let active = true

  const poll = async () => {
    try {
      const messages = await getMessages()
      if (active) onMessages(messages)
    } catch (err) {
      if (active && onError) onError(err)
    }
  }

  poll()
  const interval = setInterval(poll, POLL_MS)

  return () => {
    active = false
    clearInterval(interval)
  }
}

export async function postToScreamRoom({ text, feel }) {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ text: text || '', feel: feel || null }),
  })
  if (!res.ok) throw new Error(`scream-room POST ${res.status}`)
  return res.json()
}

// One presence heartbeat: marks me online and returns the roster
// ([{ author, online, last_seen }], online-first). Internal to subscribeToPresence.
async function heartbeatPresence() {
  const res = await fetch(PRESENCE_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: '{}',
  })
  if (!res.ok) throw new Error(`scream-room presence ${res.status}`)
  const data = await res.json()
  return Array.isArray(data.presence) ? data.presence : []
}

// Heartbeat while the tab is visible so others see me online (green). When the tab
// is hidden / navigated away, beats stop and I go "away" (red) after the server's
// stale window — but my name stays in the roster. Returning fires an immediate beat
// so I light back up. onRoster receives the latest roster on each beat.
export function subscribeToPresence(onRoster, onError) {
  let active = true

  const beat = async () => {
    if (!active || (typeof document !== 'undefined' && document.visibilityState !== 'visible')) return
    try {
      const roster = await heartbeatPresence()
      if (active) onRoster(roster)
    } catch (err) {
      if (active && onError) onError(err)
    }
  }

  const onVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') beat()
  }

  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility)
  beat()
  const interval = setInterval(beat, PRESENCE_MS)

  return () => {
    active = false
    clearInterval(interval)
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility)
  }
}
